import React, { useState, useCallback, useEffect } from "react";
import { Platform, View, Pressable, StyleSheet } from "react-native";
import { CodeIcon, PencilSimpleIcon, WarningIcon } from "@phosphor-icons/react";
import TextEditor from "./TextEditor";
import MobileTextEditor from "./MobileTextEditor";
import HtmlSourceEditor from "./HtmlSourceEditor";
import CustomText from "../common/general/CustomText/CustomText";
import CustomModal from "../common/general/CustomModal/CustomModal";
import CustomButton from "../common/general/CustomButton/CustomButton";
import CustomSpacing from "../common/layout/CustomSpacing/CustomSpacing";
import { useIframeAllowedDomains } from "../../hooks/useIframeAllowedDomains";

type EditorMode = "visual" | "html";

const CustomRichTextEditor = ({
  value,
  onChange,
  isSubmitting,
  placeholder,
  activelyEditing,
  makeImagesPublic = false,
  restrictImageUpload = false,
  allowHtmlMode = false,
  onModeChange,
  initialMode = "visual",
}: {
  value: string;
  onChange: (content: string) => void;
  isSubmitting: boolean;
  placeholder: string;
  activelyEditing: boolean;
  makeImagesPublic?: boolean;
  restrictImageUpload?: boolean;
  allowHtmlMode?: boolean;
  onModeChange?: (mode: EditorMode) => void;
  initialMode?: EditorMode;
}) => {
  const isWeb = Platform.OS === "web";
  const [editorMode, setEditorMode] = useState<EditorMode>(initialMode);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const tenantDomains = useIframeAllowedDomains();

  useEffect(() => {
    setEditorMode(initialMode);
  }, [initialMode]);

  const showModeToggle = allowHtmlMode && isWeb;

  const confirmSwitchToVisual = useCallback(() => {
    setConfirmModalVisible(false);
    setEditorMode("visual");
    onModeChange?.("visual");
  }, [onModeChange]);

  const requestModeSwitch = useCallback(
    (targetMode: EditorMode) => {
      if (targetMode === editorMode) return;

      if (targetMode === "visual" && editorMode === "html") {
        setConfirmModalVisible(true);
        return;
      }

      setEditorMode(targetMode);
      onModeChange?.(targetMode);
    },
    [editorMode, onModeChange]
  );

  const renderModeToggle = () => {
    if (!showModeToggle) return null;

    return (
      <>
        <View style={styles.toggleContainer}>
          <Pressable
            style={[styles.toggleButton, editorMode === "visual" && styles.toggleButtonActive]}
            onPress={() => requestModeSwitch("visual")}
            accessibilityLabel="Visual editor"
            role="button">
            <PencilSimpleIcon size={14} color={editorMode === "visual" ? "#344054" : "#667085"} />
            <CustomText
              text="Visual Editor"
              size="xs"
              weight={editorMode === "visual" ? "bold" : "medium"}
              useTranslationText={false}
              style={editorMode === "visual" ? styles.toggleTextActive : styles.toggleText}
            />
          </Pressable>
          <Pressable
            style={[styles.toggleButton, editorMode === "html" && styles.toggleButtonActive]}
            onPress={() => requestModeSwitch("html")}
            accessibilityLabel="HTML source editor"
            role="button">
            <CodeIcon size={14} color={editorMode === "html" ? "#344054" : "#667085"} />
            <CustomText
              text="HTML Editor"
              size="xs"
              weight={editorMode === "html" ? "bold" : "medium"}
              useTranslationText={false}
              style={editorMode === "html" ? styles.toggleTextActive : styles.toggleText}
            />
          </Pressable>
        </View>

        {editorMode === "html" && (
          <View style={styles.modeBanner}>
            <WarningIcon size={16} color="#b45309" />
            <CustomText
              text="You are in HTML mode. Your content is shown as raw HTML. Switch back to Visual to use the rich-text editor."
              size="xs"
              useTranslationText={false}
              style={styles.modeBannerText}
            />
          </View>
        )}

        <CustomModal
          visible={confirmModalVisible}
          onRequestClose={() => setConfirmModalVisible(false)}
          title="Switch to Visual Editor?"
          showCloseButton={true}
          style={{ maxWidth: 480 }}>
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text="Switching to Visual Editor will parse your HTML. Elements not supported by the visual editor (e.g. <div>, <table>, <iframe>) may be removed or altered."
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="l" />
          <View style={styles.modalActions}>
            <CustomButton text="Cancel" styleType="secondary" size="s" onPress={() => setConfirmModalVisible(false)} />
            <CustomButton text="Continue" styleType="primary" size="s" onPress={confirmSwitchToVisual} />
          </View>
        </CustomModal>
      </>
    );
  };

  const renderEditor = () => {
    if (editorMode === "html" && isWeb) {
      return (
        <HtmlSourceEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={isSubmitting}
          iframeDomainAllowlist={tenantDomains}
        />
      );
    }

    if (isWeb) {
      return (
        <TextEditor
          value={value}
          onChange={(content) => onChange(content)}
          disabled={isSubmitting}
          placeholder={placeholder}
          makeImagesPublic={makeImagesPublic}
          restrictImageUpload={restrictImageUpload}
        />
      );
    }

    return (
      <MobileTextEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        activelyEditing={activelyEditing}
        makeImagesPublic={makeImagesPublic}
        restrictImageUpload={restrictImageUpload}
      />
    );
  };

  return (
    <View>
      {renderModeToggle()}
      {renderEditor()}
    </View>
  );
};

const styles = StyleSheet.create({
  toggleContainer: {
    flexDirection: "row",
    marginBottom: 8,
    backgroundColor: "#f3f4f6",
    borderRadius: 8,
    padding: 4,
    alignSelf: "flex-start",
  },
  toggleButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  toggleButtonActive: {
    backgroundColor: "#ffffff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  toggleText: {
    color: "#667085",
  },
  toggleTextActive: {
    color: "#344054",
  },
  modeBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#fffbeb",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#fcd34d",
  },
  modeBannerText: {
    flex: 1,
    color: "#92400e",
    lineHeight: 18,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
  },
});

export default CustomRichTextEditor;
